<template>
	<div class="action-items g-2" :class="{ 'action-items--large': large }">
		<WatchNowButton
			:watchNowOffer="watchNowOffer"
			:titleObjectId="titleObject.id"
			:titleObjectType="titleObject.type"
			:title="campaignNode"
			:inQuickActions="true"
			:additionalContexts="additionalContexts"
			:isExpanded="false"
			:isSponsoredRecommendation="true"
			:lazy="lazy"
			:hideIcon="isCinemaSR"
			color="primary"
			size="large"
			:class="{ 'cinema-watchnow': isCinemaSR }"
			:promotionalProviderLogo="promotionalProviderLogo"
			:useClassicUrl="true"
			data-testid="sr__watch-now"
			@watchNowClicked="emits('onWatchNowClick')"
		>
			<template #provider-icon>
				<ProviderIcon
					v-if="!isCinemaSR"
					:providerShortName="watchNowOffer?.package.shortName"
					:lazy="lazy"
					:iconUrl="promotionalProviderLogo || watchNowOffer?.package.icon"
					class="action-items__providerIcon me-2"
					size="small"
					:data-testid="promotionalProviderLogo ? 'sr__action-provider-override' : 'sr__action-provider'"
				/>
			</template>
			<FontAwesomeIcon v-if="isCinemaSR" class="me-2 fa-regular" :icon="faTicket" />

			<span v-if="watchNowLabel">
				{{ watchNowLabel }}
			</span>
		</WatchNowButton>

		<NuxtLink
			v-if="!hideDetailPageButton"
			class="action-items__button action-items__more text-medium btn-low radius-small px-2 py-5"
			:to="campaignNode.content.fullPath"
			data-testid="sr__more-info"
			@click.native.stop="emits('onClickMore')"
		>
			{{ $t('WEBAPP_SHOW_MORE_TEXT') }}
		</NuxtLink>

		<button
			v-if="!hideBookmarkButton"
			class="action-items__button action-items__watch-list text-medium btn-low radius-small p-3"
			@click.stop="emits('toggleWatchlist', campaignNode)"
		>
			<FontAwesomeIcon
				class="action-items__watch-list-icon"
				:class="{ 'text-high-active': isInWatchlist }"
				:icon="faBookmark"
			/>
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import WatchNowButton from '@/components/buttons/WatchNowButton.vue';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/features/sponsored-recommendations/types';
import { faTicket, faBookmark } from '@fortawesome/free-solid-svg-icons';

const props = withDefaults(
	defineProps<{
		sponsoredCampaign: NonNullable<SponsoredAdFragment['campaign']>;
		isInWatchlist: boolean;
		additionalContexts?: SnowplowContext[];
		isCinemaSR?: boolean;
		lazy?: boolean;
		promotionalProviderLogo?: string | null;
		large?: boolean;
		hideBookmarkButton?: boolean;
		hideDetailPageButton?: boolean;
	}>(),
	{
		lazy: true,
		isCinemaSR: false,
		isInWatchlist: false,
		large: false,
		hideProviderIcon: false,
		hideDetailPageButton: false,
		additionalContexts: () => [],
	}
);

const emits = defineEmits<{
	(e: 'onWatchNowClick'): void;
	(e: 'onClickMore'): void;
	(e: 'toggleWatchlist', data: SingleTitleSR): void;
}>();

const campaignNode = computed<SingleTitleSR>(() => props.sponsoredCampaign?.node as SingleTitleSR);
const watchNowOffer = computed(() => props.sponsoredCampaign.watchNowOffer);
const watchNowLabel = computed(() => props.sponsoredCampaign.watchNowLabel);
const hideDetailPageButton = computed(() => props.sponsoredCampaign.hideDetailPageButton || props.hideDetailPageButton);

const titleObject = computed(() => {
	const campaignNodeContent =
		campaignNode.value.__typename === 'Season' ? campaignNode.value.show : campaignNode.value;

	return {
		id: campaignNodeContent.objectId,
		type: campaignNodeContent.objectType,
	};
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.action-items {
	display: flex;
	align-items: stretch;

	& > * {
		// this places the action buttons "above" the link of the card
		position: relative;
	}

	:deep(.basic-button) {
		background-color: var(--jw-btn-high-default);
		padding: var(--jw-spacing-2) var(--jw-spacing-3);
		border-radius: var(--jw-radius-small);
		transition: background-color ease-in-out 200ms;
		line-height: 1;
		font-size: 14px;

		&:hover {
			background-color: var(--jw-btn-high-hover);
		}

		.basic-button__content {
			.ignore-global-styles:hover {
				background-color: inherit;
			}
		}

		.provider-icon,
		.picture-element > img {
			display: flex;
			align-items: center;
		}
	}

	&--large {
		:deep(.basic-button) {
			font-size: 16px;
			padding: var(--jw-spacing-2) var(--jw-spacing-5);
		}
	}

	:deep(.watch-now-button) {
		display: flex;
		width: fit-content;
		flex: 0 0 fit-content;

		&.cinema-watchnow {
			.basic-button {
				background-color: var(--jw-yellow-500);

				&:hover {
					background-color: $jw-color-gold--dark;
				}

				&__content {
					color: var(--jw-shade-black);
				}
			}
		}
	}

	&__button {
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		border: none;
		text-wrap: nowrap;
	}

	&__more {
		font-weight: 700;
		font-size: 14px;
		line-height: 1;
		text-transform: capitalize;

		.action-items--large & {
			font-size: 16px;
		}
	}

	&__watch-list-icon {
		height: var(--jw-size-4);
		width: var(--jw-size-4);
	}

	@container srCompact (max-width: 360px) {
		::v-deep(.basic-button) {
			padding: 6px 10px;
		}

		&__button {
			padding: 6px 10px;
		}
	}
}
</style>
